"use client";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
	email,
	firstNameUserInput,
	isOpenLoginModal,
	lastNameUserInput,
	loadSessionByToken,
	LoginFormStep,
	loginFormStep,
	loginGmailEmail,
	loginWithEmail,
	loginWithPhone,
	phone,
	resendOtp,
	setEmailText,
	setFirstName,
	setIsOpenModal,
	setLastName,
	setPhoneText,
	setUserInputOTP,
	submitFirstNameAndLastName,
	validateOtp,
	isSendingOtp,
	LoginMethod,
	loginMethod,
	handlePhoneValidation,
	isAdditionalContactValidation,
	additionalContactType,
	additionalContactValue,
	isAdditionalContactValid,
	additionalContactError,
	isAdditionalOtpSent,
	additionalOtpValue,
	isAdditionalOtpValid,
	additionalOtpError,
	isSendingAdditionalOtp,
	isVerifyingAdditionalOtp,
	setAdditionalContactType,
	setAdditionalContactValue,
	sendAdditionalContactOtp,
	setAdditionalOtpValue,
	verifyAdditionalContactOtp,
	resendAdditionalOtp,
	isAdditionalContactVerified,
} from "@/store/auth.store";
import { Input } from "@/components/ui/input";
import { useSignals } from "@preact/signals-react/runtime";
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { showSuccess, showError } from "@/lib/toast-utill";
import { useEffect } from "react";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { CircleUserRound, Mail, Phone, Loader2 } from "lucide-react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { signal } from "@preact/signals-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { isValidPhoneNumber } from "react-phone-number-input";
import AppleSignin, { AppleAuthOptions, AppleAuthResponse  } from "react-apple-signin-auth";
import { generateRandomString } from "@/lib/utils";

const LoginWithEmailForm = ({ fromCheckout }) => {
	useSignals();

	useEffect(() => {
		loginMethod.value = LoginMethod.Email;
	}, []);

	return (
		<form
			onSubmit={async (event) => {
				event.preventDefault();
				await loginWithEmail();
			}}
		>
			<Input
				type="email"
				placeholder="johndoe@email.com"
				value={email?.value ?? ""}
				onChange={(e) => setEmailText(e.target.value)}
				required
				className="mt-6"
				autoComplete="email"
			/>
			<p className="text-sm text-stone-neutral-11 mt-2">
				You will receive a verification code via an email
			</p>
			<Button
				type="submit"
				className="w-full my-5"
				disabled={isSendingOtp.value}
			>
				{isSendingOtp.value ? (
					<>
						<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						Sending verification code...
					</>
				) : fromCheckout ? (
					"Continue to purchase tickets"
				) : (
					"Continue with Email"
				)}
			</Button>
		</form>
	);
};

const LoginWithPhoneForm = ({ fromCheckout }) => {
	useSignals();
	const isValid = signal(true);
	const validationError = signal("");

	useEffect(() => {
		loginMethod.value = LoginMethod.Phone;
	}, []);

	const validatePhoneNumber = (value: string): { isValid: boolean; error: string } => {
		if (!value) {
			return { isValid: true, error: "" };
		}

		// Check if phone number has country code
		if (!value.startsWith('+')) {
			return { 
				isValid: false, 
				error: "Please include country code (e.g., +1 for US, +44 for UK)" 
			};
		}

		// Check if it's a valid phone number using the plugin
		if (!isValidPhoneNumber(value)) {
			// More specific validation checks
			const countryCode = value.substring(1, 3); // Get the country code part
			
			// Check if it's too short
			if (value.length < 8) {
				return { 
					isValid: false, 
					error: "Phone number is too short. Please enter a complete number." 
				};
			}
			
			// Check if it's too long
			if (value.length > 15) {
				return { 
					isValid: false, 
					error: "Phone number is too long. Please check your number." 
				};
			}

			// Check for common patterns
			if (!/^\+[1-9]\d{1,14}$/.test(value)) {
				return { 
					isValid: false, 
					error: "Invalid phone number format. Please check your number." 
				};
			}

			return { 
				isValid: false, 
				error: "Please enter a valid phone number for your country." 
			};
		}

		return { isValid: true, error: "" };
	};

	const handlePhoneChange = (value: string = "") => {
		const validation = validatePhoneNumber(value);
		isValid.value = validation.isValid;
		validationError.value = validation.error;
		handlePhoneValidation(value);
	};

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		const validation = validatePhoneNumber(phone.value);
		if (!validation.isValid) {
			isValid.value = false;
			validationError.value = validation.error;
			return;
		}
		await loginWithPhone();
	};

	const isFormValid = phone.value && validatePhoneNumber(phone.value).isValid;

	return (
		<form onSubmit={handleSubmit}>
			<div className="mt-6">
				<div
					className={`flex items-center border border-input rounded-md px-3 w-full ${
						!isValid.value && phone.value ? "ring-2 ring-cherry-red-11" : ""
					} focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2`}>
					<PhoneInput
						international
						defaultCountry="US"
						placeholder="Enter phone number"
						value={phone.value || ""}
						onChange={handlePhoneChange}
						className="w-full [&>input]:h-10 [&>input]:w-full [&>input]:border-none [&>input]:bg-background [&>input]:text-sm [&>input]:placeholder:text-muted-foreground [&>input]:outline-none [&>input]:focus:outline-none [&>input]:focus-visible:outline-none [&>input]:focus-visible:ring-0 [&_.PhoneInputCountry]:[color-scheme:light]"
					/>
				</div>

				{!isValid.value && phone.value && validationError.value && (
					<p className="mt-1 text-sm text-cherry-red-11">
						{validationError.value}
					</p>
				)}
				<p className="text-sm text-stone-neutral-11 mt-2">
					You will receive a verification code via WhatsApp
				</p>
			</div>
			<Button
				type="submit"
				className="w-full my-5"
				disabled={!isFormValid || isSendingOtp.value}
			>
				{isSendingOtp.value ? (
					<>
						<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						Sending verification code...
					</>
				) : fromCheckout ? (
					"Continue to purchase tickets"
				) : (
					"Continue with Phone"
				)}
			</Button>
		</form>
	);
};

const LoginTabs = ({ fromCheckout }) => {
	const environment = process.env.NEXT_PUBLIC_APP_ENV;
	const isPhoneLoginEnabled = environment !== 'production';

	if (!isPhoneLoginEnabled) {
		// If phone login is disabled (production environment), render email form directly without tabs
		return (
			<div>
				<LoginWithEmailForm fromCheckout={fromCheckout} />
				<div className="relative flex items-center my-3">
					<div className="flex-grow border-t border-stone-neutral-7"></div>
					<span className="flex-shrink mx-4 text-stone-neutral-11">or</span>
					<div className="flex-grow border-t border-stone-neutral-7"></div>
				</div>
			</div>
		);
	}

	return (
		<Tabs
			defaultValue="email"
			className="w-full"
		>
			<TabsList className="grid w-full grid-cols-2 mb-6">
				<TabsTrigger
					value="email"
					className="flex items-center gap-2"
				>
					<Mail className="h-4 w-4" /> Email
				</TabsTrigger>
				<TabsTrigger
					value="phone"
					className="flex items-center gap-2"
				>
					<Phone className="h-4 w-4" /> Phone
				</TabsTrigger>
			</TabsList>
			<TabsContent value="email">
				<LoginWithEmailForm fromCheckout={fromCheckout} />
			</TabsContent>
			<TabsContent value="phone">
				<LoginWithPhoneForm fromCheckout={fromCheckout} />
			</TabsContent>
			<div className="relative flex items-center my-3">
				<div className="flex-grow border-t border-stone-neutral-7"></div>
				<span className="flex-shrink mx-4 text-stone-neutral-11">or</span>
				<div className="flex-grow border-t border-stone-neutral-7"></div>
			</div>
		</Tabs>
	);
};

const LoginWithGmail = ({ fromCheckout }) => {
	return (
		<div className="flex items-center justify-center w-full [&>div]:w-full [&>div]:rounded-[6px] [&_div>span]:text-sm">
			<GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_AUTH_CLIENT_ID}>
				<GoogleLogin
					onSuccess={(credentialResponse) => {
						const token = credentialResponse.credential;
						loginGmailEmail(token).then(console.log);
					}}
					onError={() => {
						console.log("Login Failed");
					}}
					useOneTap
					logo_alignment="center"
					text={fromCheckout ? "continue_with" : "signin_with"}
				/>
			</GoogleOAuthProvider>
		</div>
	);
};

const LoginWithApple = ({ fromCheckout }) => {
  // For Apple Sign In, the redirect URI must be a specific callback endpoint
  // This is required by Apple's OAuth 2.0 implementation, even with popup mode
  // The callback endpoint processes the auth, then communicates back to this modal
  const redirectUri = process.env.NEXT_PUBLIC_APPLE_REDIRECT_URI;
  
  console.log("Apple Redirect URI:", redirectUri);
  console.log("Apple Client ID:", process.env.NEXT_PUBLIC_APPLE_CLIENT_ID);

  useEffect(() => {
    // Listen for messages from the popup callback
    const handleMessage = (event: MessageEvent) => {
      // Verify the origin for security
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'apple-signin-success') {
        console.log('Apple Sign In successful in modal', event.data);
        // TODO: Process the authentication code
        // You would call your backend here to exchange the code for tokens
        // and create a user session
      } else if (event.data.type === 'apple-signin-error') {
        console.error('Apple Sign In failed in modal', event.data);
        showError('Apple Sign In failed. Please try again.');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  
  const authOptions: AppleAuthOptions = {
    clientId: process.env.NEXT_PUBLIC_APPLE_CLIENT_ID,
    scope: "email name",
    redirectURI: redirectUri,
    state: generateRandomString(32),
    nonce: generateRandomString(32),
    usePopup: true,
  };

  const handleAppleLogin = (response: AppleAuthResponse) => {
    console.log("Apple login successful", JSON.stringify(response));
    // Process the response from Apple
    // The response contains: authorization.code, authorization.id_token, user info
  };

  const handleAppleLoginError = (error: any) => {
    console.log("Apple login error", JSON.stringify(error));
    showError('Apple Sign In failed. Please try again.');
  };

	return (
		<div className="flex items-center justify-center w-full">
      <AppleSignin
        authOptions={authOptions}
        onSuccess={handleAppleLogin}
        onError={handleAppleLoginError}
        uiType="dark"
        buttonExtraChildren={fromCheckout ? "Continue with Apple" : "Sign in with Apple"}
        className="w-full !rounded-[6px] !text-sm [&_svg]:h-[40px]"
      />
    </div>
  );
};

const InitialLoginForm = ({ fromCheckout, variant }) => {
	if (variant === "inline") {
    return (
      <>
        <LoginTabs fromCheckout={fromCheckout} />
        <LoginWithGmail fromCheckout={fromCheckout} />
      </>
    );
  }
  return (
    <DialogHeader>
      <DialogTitle>
        {fromCheckout
          ? "Where should we send your tickets?"
          : "Welcome! Let's get started"}
      </DialogTitle>
      <DialogDescription className="text-stone-neutral-11">
        {fromCheckout
          ? "Tickets will be delivered to the contact method you provide below"
          : "New here? No problem! Sign in or create an account to get started."}
      </DialogDescription>
      <LoginTabs fromCheckout={fromCheckout} />
      <LoginWithGmail fromCheckout={fromCheckout} />
      <LoginWithApple fromCheckout={fromCheckout} />
    </DialogHeader>
  );
};

const FirstLoginForm = ({ fromCheckout, variant }) => {
  useSignals();

  const formContent = (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        await submitFirstNameAndLastName();
      }}
      className="space-y-4"
    >
      <div>
        <Label htmlFor="firstName">First name <span className="text-cherry-red-11">*</span></Label>
        <Input
          id="firstName"
          type="text"
          required
          value={firstNameUserInput.value}
          onChange={(e) => setFirstName(e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="lastName">Last name <span className="text-cherry-red-11">*</span></Label>
        <Input
          id="lastName"
          type="text"
          required
          value={lastNameUserInput.value}
          onChange={(e) => setLastName(e.target.value)}
        />
      </div>

      {/* Additional Contact Validation Section */}
      {isAdditionalContactValidation.value && (
        <div className="space-y-4 border-t pt-2">
          <div>
            <Label htmlFor="additionalContact">
              {!isAdditionalOtpSent.value && !isAdditionalContactVerified.value
                ? (
                  <>
                    {additionalContactType.value === "email"
                      ? "Email Address"
                      : "Phone Number"}{" "}
                    <span className="text-cherry-red-11">*</span>
                  </>
                )
                : `Enter the OTP sent to your ${additionalContactType.value}`}
            </Label>

            {/* 1. Contact Input */}
            {!isAdditionalOtpSent.value && !isAdditionalContactVerified.value ? (
              <div className="flex gap-2">
                {additionalContactType.value === "email" ? (
                  <Input
                    id="additionalContact"
                    type="email"
                    placeholder="Enter email address"
                    value={additionalContactValue.value}
                    onChange={(e) => setAdditionalContactValue(e.target.value)}
                    className="flex-1"
                  />
                ) : (
                  <div className="flex items-center border border-input rounded-md px-3 w-full flex-1 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                    <PhoneInput
                      international
                      defaultCountry="US"
                      placeholder="Enter phone number"
                      value={additionalContactValue.value}
                      onChange={(value) =>
                        setAdditionalContactValue(value || "")
                      }
                      className="w-full [&>input]:h-10 [&>input]:w-full [&>input]:border-none [&>input]:bg-background [&>input]:text-sm [&>input]:placeholder:text-muted-foreground [&>input]:outline-none [&>input]:focus:outline-none [&>input]:focus-visible:outline-none [&>input]:focus-visible:ring-0 [&_.PhoneInputCountry]:[color-scheme:light]"
                    />
                  </div>
                )}
              </div>
            ) : isAdditionalContactVerified.value ? (
              /* 2. Verified State */
              <div className="flex gap-2">
                {additionalContactType.value === "email" ? (
                  <Input
                    id="additionalContact"
                    type="email"
                    placeholder="Enter email address"
                    value={additionalContactValue.value}
                    onChange={(e) => setAdditionalContactValue(e.target.value)}
                    className="flex-1"
                  />
                ) : (
                  <div className="flex items-center border border-input rounded-md px-3 w-full flex-1 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                    <PhoneInput
                      international
                      defaultCountry="US"
                      placeholder="Enter phone number"
                      value={additionalContactValue.value}
                      onChange={(value) =>
                        setAdditionalContactValue(value || "")
                      }
                      className="w-full [&>input]:h-10 [&>input]:w-full [&>input]:border-none [&>input]:bg-background [&>input]:text-sm [&>input]:placeholder:text-muted-foreground [&>input]:outline-none [&>input]:focus:outline-none [&>input]:focus-visible:outline-none [&>input]:focus-visible:ring-0 [&_.PhoneInputCountry]:[color-scheme:light]"
                    />
                  </div>
                )}
                <Button
                  type="button"
                  disabled={true}
                  className="px-4 bg-green-600 hover:bg-green-700"
                >
                  Validated
                </Button>
              </div>
            ) : (
              /* 3. OTP Validation State */
              <div className="space-y-4">
                <div className="flex flex-col items-center">
                  <div className="w-full flex justify-center">
                    <InputOTP
                      onChange={setAdditionalOtpValue}
                      maxLength={6}
                      className="[&>div]:gap-2"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} className="h-12 w-12 text-lg" />
                        <InputOTPSlot index={1} className="h-12 w-12 text-lg" />
                        <InputOTPSlot index={2} className="h-12 w-12 text-lg" />
                      </InputOTPGroup>
                      <InputOTPSeparator className="w-4 flex justify-center" />
                      <InputOTPGroup>
                        <InputOTPSlot index={3} className="h-12 w-12 text-lg" />
                        <InputOTPSlot index={4} className="h-12 w-12 text-lg" />
                        <InputOTPSlot index={5} className="h-12 w-12 text-lg" />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      isAdditionalOtpSent.value = false;
                      additionalOtpValue.value = "";
                    }}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    onClick={verifyAdditionalContactOtp}
                    disabled={
                      !additionalOtpValue.value || isVerifyingAdditionalOtp.value
                    }
                    className="flex-1"
                  >
                    {isVerifyingAdditionalOtp.value ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      "Verify"
                    )}
                  </Button>
                </div>

                <div className="text-center text-sm text-stone-neutral-11">
                  Didn't receive a code?{" "}
                  <Button
                    type="button"
                    variant="link"
                    onClick={resendAdditionalOtp}
                    disabled={isSendingAdditionalOtp.value}
                    className="h-auto p-0 text-base font-semibold text-primary"
                  >
                    {isSendingAdditionalOtp.value ? "Sending..." : "Resend Code"}
                  </Button>
                </div>
              </div>
            )}

            {additionalContactError.value && (
              <p className="text-sm text-cherry-red-11 mt-1">
                {additionalContactError.value}
              </p>
            )}

            {additionalOtpError.value && (
              <p className="text-sm text-cherry-red-11 mt-1 text-center">
                {additionalOtpError.value}
              </p>
            )}
          </div>
        </div>
      )}

      <Button
        type="submit"
        disabled={
          !firstNameUserInput.value?.trim() || 
          !lastNameUserInput.value?.trim() ||
          (isAdditionalContactValidation.value && !isAdditionalContactValid.value)
        }
      >
        Continue
      </Button>
    </form>
  );

  if (variant === "inline") {
    return formContent;
  }

  return (
    <DialogHeader>
      <DialogTitle>
        {fromCheckout
          ? "Great! Now that we know where to send your tickets, what should we call you?"
          : "Welcome to Toutix"}
      </DialogTitle>
      <DialogDescription>
        Please enter your first name, last name{isAdditionalContactValidation.value ? `, and ${additionalContactType.value}` : ""} to continue
      </DialogDescription>
      {formContent}
    </DialogHeader>
  );
};


const ValidateOTP = ({ variant }) => {
  useSignals();
  const isResending = signal(false);
  const verificationMethod = loginMethod.value;
  const verificationTarget =
    verificationMethod === LoginMethod.Phone ? phone.value : email.value;

  const handleResendCode = async () => {
    try {
      isResending.value = true;
      await resendOtp();
      showSuccess(`Verification code resent to your ${verificationMethod}!`);
    } catch (error) {
      showError("Failed to resend verification code. Please try again.");
    } finally {
      isResending.value = false;
    }
  };

  const formContent = (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        try {
          await validateOtp();
        } catch (error) {
          showError("Failed to verify code. Please try again.");
        }
      }}
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col items-center">
        <div className="w-full flex justify-center">
          <InputOTP
            onChange={setUserInputOTP}
            maxLength={6}
            className="[&>div]:gap-2"
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} className="h-12 w-12 text-lg" />
              <InputOTPSlot index={1} className="h-12 w-12 text-lg" />
              <InputOTPSlot index={2} className="h-12 w-12 text-lg" />
            </InputOTPGroup>
            <InputOTPSeparator className="w-4 flex justify-center" />
            <InputOTPGroup>
              <InputOTPSlot index={3} className="h-12 w-12 text-lg" />
              <InputOTPSlot index={4} className="h-12 w-12 text-lg" />
              <InputOTPSlot index={5} className="h-12 w-12 text-lg" />
            </InputOTPGroup>
          </InputOTP>
        </div>
      </div>

      <div className="text-center text-sm text-stone-neutral-11">
        Didn't receive a code?{" "}
        <Button
          type="button"
          variant="link"
          onClick={handleResendCode}
          disabled={isResending.value}
          className="h-auto p-0 text-base font-semibold text-primary"
        >
          {isResending.value ? "Sending..." : "Resend Code"}
        </Button>
      </div>

      <Button type="submit" className="w-full h-12 text-base font-medium">
        Verify and Continue
      </Button>
    </form>
  );

  if (variant === "inline") {
    return formContent;
  }

  return (
    <DialogHeader>
      <DialogTitle>
        Verify your{" "}
        {verificationMethod === LoginMethod.Phone
          ? "Phone Number"
          : "Email Address"}
      </DialogTitle>
      <DialogDescription className="text-left">
        <div className="flex flex-col gap-4">
          <div className="text-base">
            We've sent a 6-digit verification code to:
            <div className="font-semibold mt-1">{verificationTarget}</div>
          </div>
          <div className="text-sm text-stone-neutral-11">
            Please enter the code to verify your{" "}
            {verificationMethod === LoginMethod.Email
              ? "email"
              : "via WhatsApp"}
          </div>
        </div>
      </DialogDescription>
      {formContent}
    </DialogHeader>
  );
};

const LoginModalStepMap = {
	[LoginFormStep.Initial]: InitialLoginForm,
	[LoginFormStep.ValidateOTP]: ValidateOTP,
	[LoginFormStep.FirstLoginForm]: FirstLoginForm,
};

export const LoginForm = ({ fromCheckout = false, variant = "dialog" }) => {
	useSignals();
	const DynamicStep = LoginModalStepMap[loginFormStep.value] ?? null;

	return <DynamicStep fromCheckout={fromCheckout} variant={variant} />;
};

export const LoginModal = () => {
	useSignals();
	useEffect(() => {
		loadSessionByToken();
	}, []);
	return (
		<Dialog
			open={isOpenLoginModal.value}
			onOpenChange={setIsOpenModal}
		>
			<DialogTrigger asChild>
				<Button className="bg-ocean-blue-3 hover:bg-ocean-blue-4 text-ocean-blue-9 hover:text-ocean-blue-9 border-none rounded-full gap-2 pl-2 pr-4">
					<CircleUserRound className="text-ocean-blue-9" />
					Login or Sign up
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[425px] p-5">
				<LoginForm />
			</DialogContent>
		</Dialog>
	);
};
